// Conversion entre les messages de l'interface (useChat / messageStream.js) et le
// format d'historique du serveur (PUT /api/conversations/:id/messages).
// Fonctions pures, sans état : la même liste produit toujours le même JSON, ce qui
// permet de comparer deux sauvegardes par leur signature.
//
// Serveur : {id, role, content, message_id?, ui?}
//   - message_id : identifiant de la réponse (événement `start`), qui relie l'avis 👍/👎
//   - ui : ce que l'interface doit retrouver au rechargement (raisonnement, modèle,
//     tokens, issue, erreur, visuels). L'avis n'y figure pas : le serveur le relit dans sa
//     table de feedback, source de vérité.
//   - ui.artifacts : visuels du message (spécifications validées, déjà tracées par le
//     backend) ; un visuel « en préparation » à la sauvegarde est enregistré interrompu.
//   - ui.effort, ui.compaction, ui.continuations (sprint 6) : niveau d'effort appliqué,
//     contexte condensé avant la réponse, nombre de continuations. Écrits seulement s'ils
//     existent : une conversation antérieure garde la même signature (aucune sauvegarde).
//   - ui.effortOverride, ui.effortAction (question, sprint 6) : niveau forcé par une action
//     rapide (point de situation) et nom de l'action, repris par « Régénérer » après
//     rechargement. Écrits seulement pour une question forcée.
//   - ui.fichiers (contrat v1.13) : liens vérifiés de fichiers de la réponse (photos de
//     véhicules) ; écrits seulement s'il y en a (signature inchangée pour une conversation
//     antérieure), relus avec la même lecture que l'événement file_links.

import { ARTIFACT_INTERRUPTED, normalizeCompaction } from './messageStream.js';
import { normalizeEffortInfo } from '../utils/effortPreference.js';
import { normalizeFichiers, serializeFichiers } from '../components/fichiers/fichiers.js';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
// Niveaux qu'une action rapide peut forcer sur une question (relus avec prudence)
const EFFORT_OVERRIDES = ['rapide', 'standard', 'approfondi'];

// Le serveur refuse un `ui` de plus de 200 Ko : marge de sécurité
const UI_BUDGET = 180 * 1024;
const THINKING_CLIP = 4000;

const byteLength = (text) => new Blob([text]).size;

// Visuel trop volumineux pour l'historique (dernier recours du budget `ui`)
const ARTIFACT_TOO_LARGE = "Ce visuel était trop volumineux pour être conservé dans l'historique.";
const ARTIFACT_STATUSES = ['ready', 'error'];

function serializeArtifact(artifact, dropSpec) {
  const base = {
    id: String(artifact.id),
    kind: artifact.kind ?? null,
    visual_type: artifact.visual_type ?? null,
    title: artifact.title ?? null,
    // Documents (sprint 5) : format du badge, même pour un document interrompu
    ...(artifact.format ? { format: String(artifact.format) } : {}),
  };
  if (artifact.status === 'ready' && artifact.spec && !dropSpec) {
    return { ...base, status: 'ready', spec: artifact.spec };
  }
  if (artifact.status === 'ready') return { ...base, status: 'error', message: ARTIFACT_TOO_LARGE };
  // Encore « en préparation » à la sauvegarde : forcément interrompu
  return { ...base, status: 'error', message: artifact.message ?? ARTIFACT_INTERRUPTED };
}

// Liste fermée de champs : l'état propre à l'interface pendant le flux (execStartedAt,
// progress : compteur et « étape n/N » de step_progress, contrat v1.8) n'est jamais enregistré,
// et une progression ne change pas la signature (aucune sauvegarde déclenchée)
function serializeItem(item, clipThinking) {
  if (item.type === 'step') {
    return {
      key: item.key,
      type: 'step',
      step_id: item.step_id ?? null,
      tool: item.tool ?? null,
      label: item.label ?? null,
      params: item.params ?? null,
      // Une étape encore « en cours » à la sauvegarde est forcément interrompue
      status: item.status === 'running' ? 'cancelled' : item.status,
      duree_ms: item.duree_ms ?? null,
      resume: item.resume ?? null,
      error: item.error ?? null,
    };
  }
  if (item.type === 'thinking') {
    const text = clipThinking && item.text.length > THINKING_CLIP
      ? `${item.text.slice(0, THINKING_CLIP)}…`
      : item.text;
    return { key: item.key, type: 'thinking', text };
  }
  return { key: item.key, type: 'narration', turn: item.turn ?? 0, text: item.text ?? '', visible: true };
}

function assistantUi(message, mode = 'full') {
  const reasoning = message.reasoning ?? { items: [] };
  const items = (reasoning.items ?? [])
    // Places réservées non utilisées (tours restés dans la réponse) : rien à garder
    .filter((item) => item.type !== 'narration' || (item.visible && item.text))
    .filter((item) => mode === 'full' || mode === 'clipped' || item.type !== 'thinking')
    .map((item) => serializeItem(item, mode !== 'full'));
  const artifacts = (message.artifacts ?? []).map((a) => serializeArtifact(a, mode === 'no-visual-data'));

  return {
    v: 1,
    // Rapport composé dans l'interface (DOC-17) : affiché comme un message système
    ...(message.origin === 'report' ? { origin: 'report' } : {}),
    model: message.model ?? null,
    modelId: message.modelId ?? null,
    modelLabel: message.modelLabel ?? null,
    usage: message.usage ?? null,
    stopReason: message.stopReason ?? null,
    dureeMs: message.dureeMs ?? null,
    outcome: message.outcome ?? 'done',
    error: message.error ?? null,
    reasoning: {
      status: reasoning.status === 'running' ? 'stopped' : (reasoning.status ?? 'idle'),
      startedAt: reasoning.startedAt ?? null,
      endedAt: reasoning.endedAt ?? null,
      items,
    },
    ...(artifacts.length ? { artifacts } : {}),
    ...(message.effort ? { effort: message.effort } : {}),
    ...(message.compaction ? { compaction: message.compaction } : {}),
    ...(message.continuations > 0 ? { continuations: message.continuations } : {}),
    ...(message.fichiers?.length ? { fichiers: serializeFichiers(message.fichiers) } : {}),
  };
}

// Raisonnement très long : réflexion tronquée, puis retirée ; en dernier recours, les
// données des visuels (le visuel est alors signalé comme non conservé)
function boundedUi(message) {
  for (const mode of ['full', 'clipped', 'no-thinking', 'no-visual-data']) {
    const ui = assistantUi(message, mode);
    if (byteLength(JSON.stringify(ui)) <= UI_BUDGET) return ui;
  }
  return null;
}

const asText = (content) => (typeof content === 'string' ? content : JSON.stringify(content ?? ''));

// Conversation entière : le serveur refuse plus de 5 Mo (texte + ui de chaque message,
// conversationStore.CONVERSATION_BYTES_MAX). Réflexion affichée en direct (Qwen, lot 1 vitesse) :
// jusqu'au budget `ui` de chaque réponse. Au-delà de CONVERSATION_BUDGET, la réflexion des
// réponses les plus anciennes est tronquée, puis retirée ; une conversation sous le budget est
// enregistrée à l'identique (même signature).
export const CONVERSATION_BUDGET = Math.floor(4.75 * 1024 * 1024);

const serializedBytes = (out) => byteLength(out.content) + (out.ui ? byteLength(JSON.stringify(out.ui)) : 0);

/** Messages de l'interface → corps du PUT. */
export function serializeMessages(messages) {
  const list = messages.filter((m) => m.role === 'user' || m.role === 'assistant');
  const out = list.map(serializeMessage);
  const sizes = out.map(serializedBytes);
  let total = sizes.reduce((a, b) => a + b, 0);
  if (total <= CONVERSATION_BUDGET) return out;
  for (const mode of ['clipped', 'no-thinking']) {
    for (let i = 0; i < list.length && total > CONVERSATION_BUDGET; i++) {
      const m = list[i];
      if (m.role !== 'assistant' || !m.reasoning?.items?.some((item) => item.type === 'thinking')) continue;
      const ui = assistantUi(m, mode);
      const candidate = { ...out[i], ui };
      const size = serializedBytes(candidate);
      if (size >= sizes[i]) continue;
      total -= sizes[i] - size;
      out[i] = candidate;
      sizes[i] = size;
    }
  }
  return out;
}

function serializeMessage(m) {
  if (m.role === 'user') {
    const out = { id: String(m.id), role: 'user', content: asText(m.content) };
    if (m.effortOverride) {
      out.ui = { effortOverride: String(m.effortOverride), ...(m.effortAction ? { effortAction: String(m.effortAction) } : {}) };
    }
    return out;
  }
  const out = { id: String(m.id), role: 'assistant', content: asText(m.content) };
  if (m.serverMessageId && UUID_RE.test(m.serverMessageId)) out.message_id = m.serverMessageId;
  const ui = boundedUi(m);
  if (ui) out.ui = ui;
  return out;
}

/** Signature d'une liste sérialisée : deux listes égales ne déclenchent pas de sauvegarde. */
export function signatureOf(serialized) {
  return JSON.stringify(serialized);
}

/** Messages du serveur (GET /api/conversations/:id) → messages de l'interface. */
export function deserializeMessages(serverMessages = []) {
  return serverMessages
    .filter((m) => m && (m.role === 'user' || m.role === 'assistant'))
    .map((m) => {
      if (m.role === 'user') {
        const question = { id: m.id, role: 'user', content: m.content ?? '' };
        const override = m.ui && typeof m.ui === 'object' ? m.ui.effortOverride : null;
        if (EFFORT_OVERRIDES.includes(override)) {
          question.effortOverride = override;
          if (typeof m.ui.effortAction === 'string' && m.ui.effortAction.trim()) question.effortAction = m.ui.effortAction.trim().slice(0, 80);
        }
        return question;
      }

      const ui = m.ui && typeof m.ui === 'object' ? m.ui : {};
      const reasoning = ui.reasoning ?? {};
      const feedback = m.feedback
        ? { rating: m.feedback.rating ?? null, reason: m.feedback.reason ?? null, comment: m.feedback.comment ?? '', status: 'saved' }
        : { rating: null, reason: null, comment: '', status: 'idle' };

      return {
        id: m.id,
        serverMessageId: m.message_id ?? null,
        role: 'assistant',
        ...(ui.origin === 'report' ? { origin: 'report' } : {}),
        content: m.content ?? '',
        turns: {},
        movedTurns: [],
        reasoning: {
          items: Array.isArray(reasoning.items) ? reasoning.items : [],
          startedAt: reasoning.startedAt ?? null,
          endedAt: reasoning.endedAt ?? null,
          // Jamais « en cours » au rechargement : le panneau s'affiche replié
          status: reasoning.status === 'running' ? 'stopped' : (reasoning.status ?? 'idle'),
          answerStart: null,
        },
        isStreaming: false,
        outcome: ui.outcome ?? 'done',
        model: ui.model ?? null,
        modelId: ui.modelId ?? null,
        modelLabel: ui.modelLabel ?? null,
        usage: ui.usage ?? null,
        stopReason: ui.stopReason ?? null,
        dureeMs: ui.dureeMs ?? null,
        error: ui.error ?? null,
        feedback,
        artifacts: deserializeArtifacts(ui.artifacts),
        effort: normalizeEffortInfo(ui.effort),
        compaction: normalizeCompaction(ui.compaction),
        continuations: Number.isInteger(ui.continuations) && ui.continuations > 0 ? ui.continuations : 0,
        fichiers: normalizeFichiers(ui.fichiers),
      };
    });
}

// Visuels relus de l'historique : jamais « en préparation » au rechargement
function deserializeArtifacts(list) {
  if (!Array.isArray(list)) return [];
  return list
    .filter((a) => a && typeof a === 'object' && a.id != null)
    .map((a) => {
      const ready = a.status === 'ready' && a.spec && typeof a.spec === 'object';
      return {
        id: String(a.id),
        status: ready ? 'ready' : 'error',
        kind: a.kind ?? null,
        visual_type: a.visual_type ?? null,
        title: a.title ?? null,
        spec: ready ? a.spec : null,
        message: ready ? null : (ARTIFACT_STATUSES.includes(a.status) && a.message ? a.message : ARTIFACT_INTERRUPTED),
        ...(typeof a.format === 'string' ? { format: a.format } : {}),
      };
    });
}
